import Divider from "@components/common/Divider";
import DetailsLayoutSection from "@components/pages/detail-pages/DetailsLayoutSection";
import { BUDGETS } from "@components/utils/constants";
import { sanitizeTags } from "@components/utils/sanitizeHelpers";
import { sortAccommodations, sortActivities, } from "@components/utils/sortingHelpers";
import useAppContext from "@hooks/useAppContext";
import Card from "@libs/cards/Card";
import CardWrapper from "@libs/cards/CardWrapper";
import { useCallback, useMemo, useRef, useState } from "react";

import { useRouter } from "next/router";

import {
  IAccommodationResponse,
  IActivityResponse,
  ILocationResponse,
  IPackageResponse,
} from "@customTypes/modelResponses";
import DetailsPageReviewsSection from "../common/DetailsPageReviewsSection";
import ViewAllAccommodationsLayout from '@components/packageComponents/ViewAllAccommodationsLayout'
import ViewAllActivitiesLayout from '@components/packageComponents/ViewAllActivitiesLayout'
import DangerouslySetHtml from '@libs/parser/components/DangerouslySetHtml'

interface IPackagePageMainSection {
  data: ILocationResponse & IPackageResponse;
  openViewAllActivities: (shouldOpen: boolean) => void;
  openViewAllAccommodations: (shouldOpen: boolean) => void;
  viewAllAccommodations?: boolean;
  viewAllActivities?: boolean;
}

interface IViewActivityData {
  title: string;
  activities: IActivityResponse[];
}

interface IViewAccommodationsData {
  title: string;
  accommodations: IAccommodationResponse[];
}

const PackagePageMainSection = ({
                                  data,
                                  openViewAllActivities,
                                  openViewAllAccommodations,
                                  viewAllAccommodations,
                                  viewAllActivities,
                                }: IPackagePageMainSection): JSX.Element => {
  const { isMobile } = useAppContext();

  const descriptionParentRef = useRef<HTMLDivElement>(null);
  const [viewAllActivitesData, setViewAllActivitiesData] =
    useState<IViewActivityData>({
      title: "",
      activities: [],
    });
  const [viewAllAccommodationsData, setViewAllAccommodationsData] =
    useState<IViewAccommodationsData>({
      title: "",
      accommodations: [],
    });

  const packageLocations = useMemo(() => {
    return data.locations ? data.locations : [data];
  }, [data]);

  const onExploreAllActivitiesClick = useCallback(
    (title: string, activities: any): void => {
      setViewAllActivitiesData({ title, activities });
      if (openViewAllActivities) {
        openViewAllActivities(true);
      }
    },
    []
  );

  const onExploreAllAccommodationsClick = useCallback(
    (title: string, accommodations: any): void => {
      setViewAllAccommodationsData({ title, accommodations });
      if (openViewAllAccommodations) {
        openViewAllAccommodations(true);
      }
    },
    []
  );

  const { minBudget, maxBudget } = useRouter().query;
  const urlBudget = useMemo(() => {
    const minUrlBudget = parseInt(minBudget as string);
    const maxUrlBudget = parseInt(maxBudget as string);
    if (minUrlBudget && maxUrlBudget) {
      return {
        min: minUrlBudget,
        max: maxUrlBudget,
      };
    }
    return {
      min: BUDGETS.MIN,
      max: BUDGETS.MAX,
    };
  }, [minBudget, maxBudget]);

  // START OF ACCOMMODATIONS CAROUSEL
  const accommodationsTabs = useMemo(() => {
    return packageLocations.map((location: any) => {
      const accommodationsList = sortAccommodations(
        location.accommodations,
        true
      );

      const isMinBudgetDefault = urlBudget.min === BUDGETS.MIN;
      const isMaxBudgetDefault = urlBudget.max === BUDGETS.MAX;

      const filteredAccommodationsList = urlBudget
        ? accommodationsList.filter((accommodation: any) => {
        return (
          (isMinBudgetDefault || accommodation.Rate > urlBudget.min) &&
          (isMaxBudgetDefault || accommodation.Rate < urlBudget.max)
        );
      }) ?? accommodationsList
        : accommodationsList;

      const accommodationsToDisplay = filteredAccommodationsList.length
        ? filteredAccommodationsList
        : accommodationsList;

      const isPackage = !!data.locations;

      const accommodationsCardComponents = accommodationsToDisplay.map(
        (accommodation: any, index: number) => {
          return (
            <CardWrapper key={accommodation.id} widthPercentage={80}>
              {/*
              https://cms.devcrvtestingmm.com/admin/content-manager/collectionType/api::accommodation.accommodation/39
              GET: id, title, blurb, slug, tags, media.primaryImage, rate
              */}
              <Card
                id={accommodation?.id}
                title={accommodation?.Title}
                subtitle={location.Title}
                blurb={accommodation?.Blurb}
                url={accommodation?.SEO?.Slug}
                // opensNewTab
                tags={sanitizeTags(accommodation?.tags)}
                image={
                  accommodation?.Media?.PrimaryImage.length &&
                  accommodation?.Media?.PrimaryImage[0]
                }
                imageSize={isMobile ? "medium" : "small"}
                priceRate={accommodation?.Rate}
                tagLeftTag={{
                  id: accommodation?.id,
                  title: accommodation?.Property_Category,
                }}
                isHorizontal={!isMobile}
                linkTitle={`View ${accommodation?.Property_Category}`}
                headingLevel={isPackage ? "h4" : "h3"}
                imageLoadingPriority={false}
              />
            </CardWrapper>
          );
        }
      );

      return (
        <div key={`${location.id}_location`} className="mb-8 desktop:mb-12">
          <DetailsLayoutSection
            title={isPackage ? `Best Places in ${location?.Title}` : undefined}
            subtitle={
              location?.Accommodations_Intro_Text
                ? location.Accommodations_Intro_Text
                : ""
            }
            sliderComponents={accommodationsCardComponents}
            onButtonClick={() =>
              onExploreAllAccommodationsClick(
                location?.Title,
                accommodationsToDisplay
              )
            }
          />
        </div>
      );
    });
  }, [
    viewAllAccommodations,
    urlBudget,
    isMobile,
    packageLocations,
  ]);
  // END OF ACCOMMODATIONS CAROUSEL

  // START OF ACTIVITIES CAROUSEL
  const activityTabs = useMemo(() => {
    const isPackage = !!data.locations;

    return packageLocations.map((location: any, index: number) => {
      const activitiesCardComponents = sortActivities(location.activities).map(
        (activity: any) => {
          return (
            <CardWrapper key={activity.id} widthPercentage={80}>
              {/*
              https://cms.devcrvtestingmm.com/admin/content-manager/collectionType/api::activity.activity/33
              GET: id, title, blurb, slug, tags, media.primaryImage, rate
              */}
              <Card
                id={activity.id}
                title={activity.Title}
                subtitle={location.Title}
                blurb={activity.Blurb}
                url={activity.SEO.Slug}
                // opensNewTab
                tags={sanitizeTags(activity.tags)}
                image={activity?.Media?.PrimaryImage?.[0]}
                imageSize={isMobile ? "medium" : "small"}
                linkTitle={"View Activity"}
                isHorizontal={!isMobile}
                headingLevel={isPackage ? "h4" : "h3"}
                imageLoadingPriority={false}
              />
            </CardWrapper>
          );
        }
      );

      return (
        <div key={`${location.id}_activity`} className="mb-8 desktop:mb-12">
          <DetailsLayoutSection
            title={isPackage ? `Things to Do in ${location?.Title}` : ""}
            subtitle={location?.Activities_Intro_Text}
            sliderComponents={activitiesCardComponents}
            onButtonClick={() => {
              onExploreAllActivitiesClick(location?.Title, location.activities);
            }}
          />
        </div>
      );
    });
  }, [viewAllActivities, isMobile, packageLocations]);
  // END OF ACTIVITIES CAROUSEL

  return (
    <>
      {/* Accommodations start */}
      <div className="ml-5 desktop:ml-0">
        <h2 className="mb-4 text-h2 font-semibold desktop:mt-[2.5rem]">
          Where to Stay
        </h2>
        {accommodationsTabs}
      </div>
      {viewAllAccommodations && (
        <ViewAllAccommodationsLayout
          key={`${data.id}_accommodations`}
          title={viewAllAccommodationsData.title}
          openViewAllAccommodations={openViewAllAccommodations}
          viewAllAccommodationsData={viewAllAccommodationsData.accommodations}
        />
      )}
      {/* Accommodations end */}
      <Divider variant="short"/>
      {/* Activities start */}
      <div className="ml-5 desktop:ml-0">
        <h2 className="mb-4 text-h2 font-semibold desktop:mt-[2.5rem]">
          Things to Do
        </h2>
        {activityTabs}
      </div>
      {viewAllActivities && (
        <ViewAllActivitiesLayout
          key={`${data.id}_activities`}
          title={viewAllActivitesData.title}
          viewAllActivities={viewAllActivities}
          openViewAllActivities={openViewAllActivities}
          viewAllActivitesData={viewAllActivitesData.activities}
        />
      )}
      {/* Activities end */}

      {data.Long_Description && (
        <>
          <Divider variant="short"/>
          <div className="mx-5 desktop:mx-0 desktop:mt-[2.5rem] desktop:block desktop:max-w-[100%]">
            <div className="mb-12 w-[100%] desktop:mb-[2.5rem]" ref={descriptionParentRef}>
              <DangerouslySetHtml html={data.Long_Description} parentRef={descriptionParentRef}/>
            </div>
          </div>
        </>
      )}

      {(data?.reviews?.length || 0) > 0 && (
        <>
          <div className="w-full">
            <Divider variant="short"/>
          </div>
          <DetailsPageReviewsSection
            reviewsData={data.reviews}
            title={data.Title}
          />
        </>
      )}
    </>
  );
};

export default PackagePageMainSection;
